import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Select from "react-select";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  ChartOfAccountStyles,
  formatDate,
  money,
  num,
} from "../../Accounts/Chart-Of-Account/ChartOfAccountShared";
import {
  FinancialStatementStyles,
  PresetChips,
  periodPresets,
  printStatement,
} from "./FinancialStatementShared";

const TITLE = "Daily Sales Report Company Wise";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const qty = (value) =>
  value === null || value === undefined
    ? "–"
    : num(value).toLocaleString("en-US", { maximumFractionDigits: 2 });

const pct = (value) =>
  value === null || value === undefined ? "–" : `${num(value).toFixed(2)}%`;

const moneyOrDash = (value, show = true) =>
  !show || Math.abs(num(value)) < 0.005 ? "–" : money(value);

const csvCell = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const DailySalesStyles = () => (
  <style>
    {`
      .ds-table { width: 100%; margin: 0; border-collapse: collapse; }
      .ds-table thead th {
        padding: 10px 12px;
        background: #2a2962;
        color: #fff;
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        white-space: nowrap;
        border: 0;
      }
      .ds-table thead th.coa-num { text-align: right; }
      .ds-table td {
        padding: 8px 12px;
        border-bottom: 1px solid #f0f1f6;
        font-size: 0.85rem;
        vertical-align: middle;
      }
      .ds-table td.coa-num { white-space: nowrap; }
      .ds-table tbody tr:hover td { background: #fafbfd; }
      .ds-table tfoot td {
        padding: 11px 12px;
        background: #f2f3f9;
        border-top: 2px solid #2a2962;
        font-weight: 700;
        color: #2a2962;
      }
      .ds-sub { display: block; font-size: 0.7rem; color: #8b90a5; }
      .ds-muted { color: #b6bacb; }
      .ds-badge {
        display: inline-block;
        margin-left: 6px;
        padding: 1px 7px;
        border-radius: 8px;
        background: #fdf3e2;
        color: #c77700;
        font-size: 0.64rem;
        font-weight: 600;
        white-space: nowrap;
      }
      .ds-bar {
        position: relative;
        min-width: 110px;
        height: 18px;
        border-radius: 9px;
        background: #f2f3f9;
        overflow: hidden;
      }
      .ds-bar-fill {
        position: absolute;
        inset: 0 auto 0 0;
        border-radius: 9px;
        background: #2a2962;
      }
      .ds-bar.is-done .ds-bar-fill { background: #1f9d55; }
      .ds-bar.is-low .ds-bar-fill { background: #e42320; }
      .ds-bar-label {
        position: relative;
        display: block;
        text-align: center;
        font-size: 0.7rem;
        font-weight: 700;
        line-height: 18px;
        color: #2a2962;
      }
      .ds-bar-fill + .ds-bar-label { text-shadow: 0 0 3px #fff; }
      .ds-search { position: relative; min-width: 220px; }
      .ds-search i {
        position: absolute; top: 50%; left: 10px;
        transform: translateY(-50%); color: #b6bacb;
      }
      .ds-search .form-control { padding-left: 30px; height: 32px; font-size: 0.8rem; }
    `}
  </style>
);

const AchievementBar = ({ value }) => {
  if (value === null || value === undefined) {
    return <span className="ds-muted">No target</span>;
  }
  const n = num(value);
  const state = n >= 100 ? "is-done" : n < 50 ? "is-low" : "";
  return (
    <div className={`ds-bar ${state}`} title={`${n.toFixed(2)}% of target`}>
      <span
        className="ds-bar-fill"
        style={{ width: `${Math.min(100, Math.max(0, n))}%` }}
      ></span>
      <span className="ds-bar-label">{n.toFixed(1)}%</span>
    </div>
  );
};

const DailySalesCompanyWise = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [, lastMonthFrom, lastMonthTo] = periodPresets()[1];
  const [inputs, setInputs] = useState({
    start_date: searchParams.get("start_date") || lastMonthFrom,
    end_date: searchParams.get("end_date") || lastMonthTo,
    vendor_id: searchParams.get("vendor_id") || "",
    business_type: searchParams.get("business_type") || "",
  });
  const [vendors, setVendors] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [search, setSearch] = useState("");
  const [onlyPriced, setOnlyPriced] = useState(false);

  const fetchData = async (params = inputs) => {
    setLoading(true);
    setAlert(null);

    const query = { start_date: params.start_date, end_date: params.end_date };
    if (params.vendor_id) query.vendor_id = params.vendor_id;
    if (params.business_type) query.business_type = params.business_type;
    setSearchParams(query, { replace: true });

    try {
      const res = await Http.get("/daily-sales-report-company-wise", {
        params: query,
      });
      setData(res.data);
    } catch (error) {
      console.error("Fetch error:", error);
      setData(null);
      setAlert({
        message:
          error.response?.data?.message ||
          "Could not load the report. Please try again.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    Http.get("/vendorSelect2")
      .then((res) => setVendors(res.data || []))
      .catch((e) => console.error("Fetch error:", e));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const apply = (changes) => {
    const next = { ...inputs, ...changes };
    setInputs(next);
    fetchData(next);
  };

  const rows = useMemo(() => {
    const list = data ? data.rows : [];
    const query = search.trim().toLowerCase();
    return list.filter(
      (row) =>
        (!onlyPriced || row.has_purchase) &&
        (!query ||
          `${row.code} ${row.name}`.toLowerCase().includes(query)),
    );
  }, [data, search, onlyPriced]);

  const totals = useMemo(
    () => ({
      target: rows.reduce((sum, row) => sum + num(row.target_qty), 0),
      targetedQty: rows
        .filter((row) => num(row.target_qty) > 0)
        .reduce((sum, row) => sum + num(row.qty), 0),
      qty: rows.reduce((sum, row) => sum + num(row.qty), 0),
      amount: rows.reduce((sum, row) => sum + num(row.amount), 0),
      salesValue: rows.reduce((sum, row) => sum + num(row.sales_value), 0),
      unpriced: rows.filter((row) => !row.has_purchase).length,
      withTarget: rows.filter((row) => num(row.target_qty) > 0).length,
    }),
    [rows],
  );

  const overallAchievement =
    totals.target > 0 ? (totals.targetedQty * 100) / totals.target : null;

  const selectedVendor =
    vendors.find((v) => String(v.value) === String(inputs.vendor_id)) || null;

  const businessTypes = data ? data.business_types : [];
  const businessTypeLabel = (value) =>
    businessTypes.find((type) => type.value === value)?.label || "All";

  const valuedAt = data?.vendor
    ? `valued at ${data.vendor.name}'s last purchase rate`
    : "valued at the last purchase rate from any vendor";

  const targetMonth = data
    ? `${MONTHS[data.target_month - 1]} ${data.target_year}`
    : "";

  const periodLabel = data
    ? `${formatDate(data.start_date)} – ${formatDate(data.end_date)}`
    : "";

  const columns = [
    ["Code", (r) => r.code],
    ["Product", (r) => r.name],
    ["Target Qty", (r) => r.target_qty ?? ""],
    ["Achievement Qty", (r) => r.qty],
    ["Purchase Rate", (r) => r.purchase_rate],
    ["VAT %", (r) => r.vat_percent],
    ["VAT / Unit", (r) => r.vat_per_unit],
    ["Unit Cost", (r) => r.unit_cost],
    ["Amount", (r) => r.amount],
    ["Achievement %", (r) => r.achievement_percent ?? ""],
    ["Remaining %", (r) => r.remaining_percent ?? ""],
  ];

  const exportCsv = () => {
    const lines = [
      [TITLE],
      [`Period: ${data.start_date} to ${data.end_date}`],
      [`Vendor: ${data.vendor?.name || "Any vendor"}`],
      [`Business type: ${businessTypeLabel(data.business_type)}`],
      [],
      columns.map(([header]) => header),
      ...rows.map((row) => columns.map(([, value]) => value(row))),
      [],
      ["", "Total", totals.target, totals.qty, "", "", "", "", totals.amount.toFixed(2)],
    ];
    const csv = lines.map((line) => line.map(csvCell).join(",")).join("\n");
    const blob = new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `daily-sales-company-wise_${data.start_date}_${data.end_date}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const headHtml = columns
      .map(
        ([header], i) =>
          `<th class="${i > 1 ? "num" : ""}">${header}</th>`,
      )
      .join("");
    const bodyHtml =
      rows
        .map(
          (row) => `
        <tr>
          <td class="code">${row.code}</td>
          <td>${row.name}</td>
          <td class="num">${qty(row.target_qty)}</td>
          <td class="num">${qty(row.qty)}</td>
          <td class="num">${moneyOrDash(row.purchase_rate, row.has_purchase)}</td>
          <td class="num">${row.has_purchase ? pct(row.vat_percent) : "–"}</td>
          <td class="num">${moneyOrDash(row.vat_per_unit, row.has_purchase)}</td>
          <td class="num">${moneyOrDash(row.unit_cost, row.has_purchase)}</td>
          <td class="num">${moneyOrDash(row.amount, row.has_purchase)}</td>
          <td class="num">${pct(row.achievement_percent)}</td>
          <td class="num">${pct(row.remaining_percent)}</td>
        </tr>`,
        )
        .join("") +
      `<tr class="grand">
        <td colspan="2">Total</td>
        <td class="num">${qty(totals.target)}</td>
        <td class="num">${qty(totals.qty)}</td>
        <td colspan="4"></td>
        <td class="num">${money(totals.amount)}</td>
        <td class="num">${pct(overallAchievement)}</td>
        <td></td>
      </tr>`;

    printStatement({
      title: TITLE,
      subtitle: `${periodLabel} &middot; ${data.vendor?.name || "Any vendor"} &middot; ${businessTypeLabel(data.business_type)} distributors &middot; Target: ${targetMonth}`,
      headHtml,
      bodyHtml,
      note: `Transferred sales ${valuedAt}, VAT included. Targets are for ${targetMonth}, the month of the start date.`,
    });
  };

  const stat = (label, icon, value, sub, className = "") => (
    <div className="col-md-3 mg-b-10">
      <div className={`coa-stat ${className}`}>
        <i className={`fa ${icon} coa-stat-icon`}></i>
        <span className="coa-stat-label">{label}</span>
        <span className="coa-stat-value">{value}</span>
        <span className="coa-stat-sub">{sub}</span>
      </div>
    </div>
  );

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <FinancialStatementStyles />
        <DailySalesStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">{TITLE}</h5>
            <p className="text-muted mb-0 tx-12">
              {data
                ? `Transferred sales ${periodLabel}, ${valuedAt}`
                : "Transferred sales valued at purchase rate"}
            </p>
          </div>
          <div className="d-flex gap-2 coa-no-print">
            <Link to="/reports" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Reports
            </Link>
            <button
              className="btn btn-outline-secondary"
              onClick={() => fetchData()}
              title="Reload data"
              disabled={loading}
            >
              <i className="fa fa-refresh"></i>
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={exportCsv}
              disabled={loading || !rows.length}
            >
              <i className="fa fa-file-excel-o mg-r-6"></i>Export CSV
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePrint}
              disabled={loading || !data}
            >
              <i className="fa fa-print mg-r-6"></i>Print
            </button>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="coa-panel coa-no-print">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-filter me-2"></i>Filters
              </span>
              <span className="coa-head-note">
                Orders transferred between the dates, both days included
              </span>
            </div>
            <div className="coa-panel-body">
              <div className="row align-items-start">
                <div className="col-lg-3">
                  <label>Vendor (Company)</label>
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Any vendor"
                    options={vendors}
                    value={selectedVendor}
                    onChange={(option) =>
                      setInputs((values) => ({
                        ...values,
                        vendor_id: option ? option.value : "",
                      }))
                    }
                    isClearable
                    isSearchable
                  />
                  <small className="coa-hint">
                    Sets the purchase rate used to value sales
                  </small>
                </div>
                <div className="col-lg-2 mg-t-10 mg-lg-t-0">
                  <label>Business Type</label>
                  <select
                    className="form-control"
                    name="business_type"
                    value={inputs.business_type}
                    onChange={handleChange}
                  >
                    <option value="">All (Total)</option>
                    {businessTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-5 mg-t-10 mg-lg-t-0">
                  <label>Transfer Date</label>
                  <div className="d-flex gap-2">
                    <input
                      type="date"
                      className="form-control"
                      name="start_date"
                      value={inputs.start_date}
                      onChange={handleChange}
                      max={inputs.end_date}
                      aria-label="Start date"
                    />
                    <input
                      type="date"
                      className="form-control"
                      name="end_date"
                      value={inputs.end_date}
                      onChange={handleChange}
                      min={inputs.start_date}
                      aria-label="End date"
                    />
                  </div>
                  <PresetChips
                    presets={periodPresets().slice(0, 2)}
                    isActive={([, from, to]) =>
                      inputs.start_date === from && inputs.end_date === to
                    }
                    onPick={([, from, to]) =>
                      apply({ start_date: from, end_date: to })
                    }
                  />
                </div>
                <div className="col-lg-2 mg-t-10 mg-lg-t-0">
                  <label>&nbsp;</label>
                  <button
                    className="btn btn-primary"
                    onClick={() => fetchData()}
                    disabled={loading || !inputs.start_date || !inputs.end_date}
                  >
                    <i
                      className={`fa ${loading ? "fa-spinner fa-spin" : "fa-search"} mg-r-6`}
                    ></i>
                    {loading ? "Loading..." : "Apply"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {data && (
            <div className="row mg-b-20">
              {stat(
                "Quantity Sold",
                "fa-cubes",
                qty(totals.qty),
                `${rows.length} product${rows.length === 1 ? "" : "s"} transferred`,
              )}
              {stat(
                "Target Achievement",
                "fa-bullseye",
                pct(overallAchievement),
                totals.withTarget
                  ? `${qty(totals.targetedQty)} of ${qty(totals.target)} on ${totals.withTarget} targeted product${totals.withTarget === 1 ? "" : "s"}`
                  : `No targets set for ${targetMonth}`,
                overallAchievement === null
                  ? ""
                  : overallAchievement >= 100
                    ? "is-ok"
                    : "is-alert",
              )}
              {stat(
                "Amount at Purchase Rate",
                "fa-money",
                money(totals.amount),
                "Including VAT",
              )}
              {stat(
                "Sales Value",
                "fa-line-chart",
                money(totals.salesValue),
                "Invoiced price of the same orders",
              )}
            </div>
          )}

          {data && totals.unpriced > 0 && (
            <div className="coa-notice is-alert">
              <i className="fa fa-exclamation-triangle"></i>
              <span>
                {totals.unpriced} product{totals.unpriced === 1 ? " has" : "s have"}{" "}
                no purchase from{" "}
                {data.vendor ? <strong>{data.vendor.name}</strong> : "any vendor"},
                so {totals.unpriced === 1 ? "its" : "their"} amount is counted
                as zero.
              </span>
            </div>
          )}

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-table me-2"></i>Product-wise Sales
                {data && (
                  <span className="coa-head-note mg-l-10">
                    Targets for {targetMonth} (month of the start date)
                  </span>
                )}
              </span>
              {data && (
                <span className="d-flex align-items-center gap-3 coa-no-print">
                  <label className="fs-switch">
                    <input
                      type="checkbox"
                      checked={onlyPriced}
                      onChange={(e) => setOnlyPriced(e.target.checked)}
                    />
                    Only priced products
                  </label>
                  <div className="ds-search">
                    <i className="fa fa-search" aria-hidden="true"></i>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search product"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      aria-label="Search product"
                    />
                  </div>
                </span>
              )}
            </div>

            {loading && !data ? (
              <div className="pd-y-10">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="fs-skeleton"></div>
                ))}
              </div>
            ) : !data ? (
              <div className="coa-empty">
                <i className="fa fa-exclamation-circle"></i>
                The report could not be loaded.
              </div>
            ) : !rows.length ? (
              <div className="coa-empty">
                <i className="fa fa-inbox"></i>
                {data.rows.length
                  ? "No products match the search."
                  : `No transferred sales between ${periodLabel}.`}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="ds-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Product</th>
                      <th className="coa-num">Target Qty</th>
                      <th className="coa-num">Achievement Qty</th>
                      <th className="coa-num">Purchase Rate</th>
                      <th className="coa-num">VAT</th>
                      <th className="coa-num">Unit Cost</th>
                      <th className="coa-num">Amount</th>
                      <th>Achievement</th>
                      <th className="coa-num">Remaining</th>
                    </tr>
                  </thead>
                  <tbody style={{ opacity: loading ? 0.5 : 1 }}>
                    {rows.map((row) => (
                      <tr key={row.product_id}>
                        <td>
                          <span className="coa-code">{row.code}</span>
                        </td>
                        <td>
                          <span style={{ fontWeight: 600, color: "#2a2962" }}>
                            {row.name}
                          </span>
                          {!row.has_purchase && (
                            <span className="ds-badge">No purchase rate</span>
                          )}
                          {row.purchase && (
                            <span className="ds-sub">
                              {row.purchase.receipt_no} ·{" "}
                              {formatDate(row.purchase.date)}
                              {!data.vendor && ` · ${row.purchase.vendor}`}
                            </span>
                          )}
                        </td>
                        <td className="coa-num">
                          <span className={row.target_qty ? "" : "ds-muted"}>
                            {qty(row.target_qty)}
                          </span>
                        </td>
                        <td className="coa-num" style={{ fontWeight: 600 }}>
                          {qty(row.qty)}
                        </td>
                        <td className="coa-num">
                          {moneyOrDash(row.purchase_rate, row.has_purchase)}
                        </td>
                        <td className="coa-num">
                          {moneyOrDash(row.vat_per_unit, row.has_purchase)}
                          {row.has_purchase && (
                            <span className="ds-sub">{pct(row.vat_percent)}</span>
                          )}
                        </td>
                        <td className="coa-num">
                          {moneyOrDash(row.unit_cost, row.has_purchase)}
                        </td>
                        <td
                          className="coa-num"
                          style={{ fontWeight: 700, color: "#2a2962" }}
                        >
                          {moneyOrDash(row.amount, row.has_purchase)}
                        </td>
                        <td>
                          <AchievementBar value={row.achievement_percent} />
                        </td>
                        <td className="coa-num">{pct(row.remaining_percent)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2} style={{ textAlign: "right" }}>
                        Total
                      </td>
                      <td className="coa-num">{qty(totals.target)}</td>
                      <td className="coa-num">{qty(totals.qty)}</td>
                      <td colSpan={3}></td>
                      <td className="coa-num">{money(totals.amount)}</td>
                      <td>
                        <AchievementBar value={overallAchievement} />
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailySalesCompanyWise;
